import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';

import {
  clientesApi,
  ListClientesParams,
} from '../../services/api/clientes.api';
import {
  ClienteDetail,
  ClienteListItem,
  ClientesFilters,
  PaginatedClientes,
} from '../../types/cliente';

export interface ClientesState {
  items: ClienteListItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  filters: ClientesFilters;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  selectedDetail: ClienteDetail | null;
  detailStatus: 'idle' | 'loading' | 'error';
  detailError: string | null;
}

const initialState: ClientesState = {
  items: [],
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  filters: {},
  status: 'idle',
  error: null,
  selectedDetail: null,
  detailStatus: 'idle',
  detailError: null,
};

export const messageFromError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined;
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join('; ') : data.message;
    }
    if (error.response?.status === 401) return 'Sua sessão expirou.';
    if (error.response?.status === 403)
      return 'Você não tem permissão para acessar este recurso.';
    if (error.code === 'ERR_NETWORK')
      return 'Não foi possível conectar ao servidor.';
  }
  return fallback;
};

export const fetchClientesThunk = createAsyncThunk<
  PaginatedClientes,
  ListClientesParams,
  { rejectValue: string }
>('clientes/fetch', async (params, { rejectWithValue }) => {
  try {
    return await clientesApi.list(params);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar clientes'));
  }
});

export const fetchClienteDetailThunk = createAsyncThunk<
  ClienteDetail,
  string,
  { rejectValue: string }
>('clientes/fetchDetail', async (id, { rejectWithValue }) => {
  try {
    return await clientesApi.detail(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar cliente'));
  }
});

const clientesSlice = createSlice({
  name: 'clientes',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<ClientesFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    clearFilters(state) {
      state.filters = {};
      state.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = Math.max(1, action.payload);
    },
    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload;
      state.page = 1;
    },
    clearSelectedDetail(state) {
      state.selectedDetail = null;
      state.detailStatus = 'idle';
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientesThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchClientesThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.items = action.payload.data;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchClientesThunk.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? 'Erro ao carregar clientes';
      })

      .addCase(fetchClienteDetailThunk.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchClienteDetailThunk.fulfilled, (state, action) => {
        state.detailStatus = 'idle';
        state.selectedDetail = action.payload;
      })
      .addCase(fetchClienteDetailThunk.rejected, (state, action) => {
        state.detailStatus = 'error';
        state.detailError = action.payload ?? 'Erro ao carregar cliente';
      });
  },
});

export const { setFilters, clearFilters, setPage, setLimit, clearSelectedDetail } =
  clientesSlice.actions;

export default clientesSlice.reducer;
